/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import mapValues from 'lodash/mapValues'
import { Modal, Form, Popconfirm, message, Button, Icon } from 'antd'
import noop from 'lodash/noop'
import { Formik } from 'formik'
import { insuranceAuthorizationShape } from '../prop-types'
import { formElementPropsGenerator } from '../../lib/forms'
import { Input, TextArea, UploadFormikFile } from '../Forms'
import api from '../../api'
import DownloadLink from '../Common/DownloadLink'
import filenameFromBlobPath from '../../lib/active-storage'

const SUPPORTED_TYPE = 'application/pdf'

const LABELS = {
  insuranceAuthorizationNumber: 'Insurance authorization #',
  notes: 'Notes',
  notesFilePath: 'Notes file',
  insuranceAuthorizationFilePath: 'Insurance authorization file',
}

const INITIAL_VALUES = mapValues(LABELS, () => '')

const propsFor = formElementPropsGenerator({
  formName: 'insurance-auth-form',
  labels: LABELS,
  placeholders: { insuranceAuthorizationNumber: 'AR12345' },
})

function PdfLinkBelowInput({ filePath }) {
  if (!filePath) return null
  const filename = filenameFromBlobPath(filePath)
  return (
    <div style={{ marginTop: -8, marginBottom: 12 }}>
      <DownloadLink path={filePath}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Icon type="file-pdf" style={{ fontSize: 18 }} />
          <span>{filename}</span>
        </span>
      </DownloadLink>
    </div>
  )
}

PdfLinkBelowInput.propTypes = {
  filePath: PropTypes.string,
}

PdfLinkBelowInput.defaultProps = {
  filePath: undefined,
}

const getInitialValues = insuranceAuth => {
  if (insuranceAuth) {
    return {
      ...insuranceAuth,
      insuranceAuthorizationNumber:
        insuranceAuth.insuranceAuthorizationNumber || '',
      notes: insuranceAuth.notes || '',
      notesFilePath: insuranceAuth.notesFilePath || undefined,
      insuranceAuthorizationFilePath:
        insuranceAuth.insuranceAuthorizationFilePath || undefined,
    }
  }
  return INITIAL_VALUES
}

export default function InsuranceAuthorizationModal({
  preAuthorizationId,
  onFinish,
  visible,
  insuranceAuth,
}) {
  const handleSave = useCallback(
    async ({
      insuranceAuthorizationNumber,
      notes,
      notesFilePath,
      insuranceAuthorizationFilePath,
    }) => {
      const cleanedNotes = notes?.trim() || ''
      const cleanedInsuranceAuthorizationNumber =
        insuranceAuthorizationNumber?.trim() || ''

      try {
        const fd = new FormData()
        // Avoid sending empty strings; treat them as nil on the server by omitting.
        // If the record currently has a value and the user clears it, send an empty
        // string so the backend can clear it (and normalize blank -> nil).
        if (cleanedNotes) {
          fd.append('notes', cleanedNotes)
        } else if (insuranceAuth?.notes) {
          fd.append('notes', '')
        }

        if (Array.isArray(notesFilePath)) {
          fd.append('notes_file', notesFilePath[0])
        }

        if (cleanedInsuranceAuthorizationNumber) {
          fd.append(
            'insurance_authorization_number',
            cleanedInsuranceAuthorizationNumber,
          )
        } else if (insuranceAuth?.insuranceAuthorizationNumber) {
          fd.append('insurance_authorization_number', '')
        }

        if (Array.isArray(insuranceAuthorizationFilePath)) {
          fd.append(
            'insurance_authorization_file',
            insuranceAuthorizationFilePath[0],
          )
        }

        await api.insuranceAuthorization.update(preAuthorizationId, fd)
        await onFinish(true)

        message.success('Insurance authorization was added successfully.')
      } catch (e) {
        message.error(
          `There was an error adding the insurance authorization: ${e.message}`,
        )
      }
    },
    [insuranceAuth, onFinish, preAuthorizationId],
  )
  const handleCancel = useCallback(() => onFinish(false), [onFinish])
  const handleDeleteInsurance = useCallback(async () => {
    try {
      await api.insuranceAuthorization.destroy(preAuthorizationId)
      await onFinish(true)

      message.success('Insurance authorization successfully removed')
    } catch (e) {
      message.error(
        `There was an error removing this Insurance authorization: ${e.message}`,
      )
    }
  }, [onFinish, preAuthorizationId])

  return (
    <Formik
      initialValues={getInitialValues(insuranceAuth)}
      onSubmit={handleSave}
    >
      {({ handleSubmit, isSubmitting, values }) => {
        const existingNotesFilePath = insuranceAuth?.notesFilePath
        const existingInsuranceAuthorizationFilePath =
          insuranceAuth?.insuranceAuthorizationFilePath

        // While editing (before save), we can't link to the newly selected file yet.
        // But we can still show the existing file link (if any).
        const notesFilePathToLink = existingNotesFilePath
        const insuranceAuthFilePathToLink = existingInsuranceAuthorizationFilePath

        const notesSelected = Array.isArray(values?.notesFilePath)
          ? values.notesFilePath[0]?.name
          : null
        const insuranceSelected = Array.isArray(
          values?.insuranceAuthorizationFilePath,
        )
          ? values.insuranceAuthorizationFilePath[0]?.name
          : null

        return (
          <Modal
            title="Insurance Authorization"
            visible={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText="Save"
            confirmLoading={isSubmitting}
            centered
            destroyOnClose
          >
            <Form onSubmit={handleSubmit}>
              <TextArea {...propsFor('notes')} label={LABELS.notes} />
              <PdfLinkBelowInput filePath={notesFilePathToLink} />
              {notesSelected && (
                <div style={{ marginTop: -8, marginBottom: 12, color: '#666' }}>
                  Selected: {notesSelected}
                </div>
              )}
              <UploadFormikFile
                name="notesFilePath"
                label={LABELS.notesFilePath}
                supportFiles={[SUPPORTED_TYPE]}
              />
              <Input
                {...propsFor('insuranceAuthorizationNumber')}
                label={LABELS.insuranceAuthorizationNumber}
              />
              <PdfLinkBelowInput filePath={insuranceAuthFilePathToLink} />
              {insuranceSelected && (
                <div style={{ marginTop: -8, marginBottom: 12, color: '#666' }}>
                  Selected: {insuranceSelected}
                </div>
              )}
              <UploadFormikFile
                name="insuranceAuthorizationFilePath"
                label={LABELS.insuranceAuthorizationFilePath}
                supportFiles={[SUPPORTED_TYPE]}
              />
              {insuranceAuth && (
                <Popconfirm
                  title="Are you sure？"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleDeleteInsurance}
                >
                  <Button size="small" type="link" icon="delete">
                    Delete Authorization
                  </Button>
                </Popconfirm>
              )}
            </Form>
          </Modal>
        )
      }}
    </Formik>
  )
}

InsuranceAuthorizationModal.propTypes = {
  preAuthorizationId: PropTypes.string.isRequired,
  insuranceAuth: insuranceAuthorizationShape,
  onFinish: PropTypes.func,
  visible: PropTypes.bool,
}

InsuranceAuthorizationModal.defaultProps = {
  onFinish: noop,
  insuranceAuth: null,
  visible: false,
}
